import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import SectionRoster from "./SectionRoster";
import { syLabel } from "../../utils/schoolYear";
import s from "./SectionRosterModal.module.css";

const AVATAR_COLORS = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];
function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}

/**
 * Centered modal that surfaces a section's roster as the primary content,
 * with section context (name, grade, faculty, adviser, term) in the
 * header. Wraps SectionRoster in default-open + no-disclosure mode.
 *
 * Props:
 *   open               boolean
 *   section            { id, name } | null
 *   gradeLabel         string | null  — e.g. "Grade 5"
 *   facultyLabel       string | null  — e.g. "Elementary"
 *   adviserName        string | null
 *   schoolYearId       number | null
 *   semesterId         number | null
 *   schoolYearLabel    string | null  — for header display
 *   semesterLabel      string | null  — for header display
 *   students           Array<Student>
 *   enrollments        Array<Enrollment>
 *   onClose            fn()
 *   onManage           fn() — deep-link to Directory > Enrollments
 */
export default function SectionRosterModal({
  open,
  section,
  gradeLabel,
  facultyLabel,
  adviserName,
  schoolYearId,
  semesterId,
  schoolYearLabel,
  semesterLabel,
  students,
  enrollments,
  // Optional context arrays — forwarded to SectionRoster so the
  // student-detail modal it opens can resolve current term, homeroom,
  // enrollment history. Missing arrays gracefully degrade.
  sections    = [],
  gradeLevels = [],
  schoolYears = [],
  semesters   = [],
  rooms       = [],
  onClose,
  onManage,
}) {
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open || !section) return null;

  const title = gradeLabel ? `${gradeLabel} · ${section.name}` : section.name;
  const avatarText = initials(section.name);

  return createPortal(
    <div
      className={s.backdrop}>
      <div
        className={s.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="srm-title">
        <div className={s.head}>
          <div
            className={s.avatar}
            style={{ background: avatarColor(section.name) }}
            aria-hidden="true">
            {avatarText}
          </div>
          <div className={s.headMain}>
            <div className={s.eyebrow}>Section roster</div>
            <h3 id="srm-title" className={s.title}>{title}</h3>
            <div className={s.metaRow}>
              {facultyLabel && (
                <span className={s.metaPill}>{facultyLabel}</span>
              )}
              {adviserName && (
                <>
                  <span>Adviser <strong>{adviserName}</strong></span>
                </>
              )}
              {(schoolYearLabel || semesterLabel) && (
                <>
                  <span className={s.metaSep}>·</span>
                  <span className={s.metaPillAccent}>
                    {schoolYearLabel ? syLabel(schoolYearLabel) : ""}
                    {schoolYearLabel && semesterLabel ? " · " : ""}
                    {semesterLabel || ""}
                  </span>
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            className={s.closeBtn}
            onClick={onClose}
            aria-label="Close">
            ✕
          </button>
        </div>

        <div className={s.body}>
          <SectionRoster
            sectionId={section.id}
            schoolYearId={schoolYearId ?? null}
            semesterId={semesterId ?? null}
            students={students}
            enrollments={enrollments}
            sections={sections}
            gradeLevels={gradeLevels}
            schoolYears={schoolYears}
            semesters={semesters}
            rooms={rooms}
            onManage={onManage}
            defaultOpen
            hideDisclosure
            hideManageButton
          />
        </div>

        <div className={s.foot}>
          <button type="button" className={s.btnGhost} onClick={onClose}>
            Close
          </button>
          <Link
            to={`/academics/sections/${section.id}`}
            className={s.btnGhost}
            onClick={onClose}>
            Open full page →
          </Link>
          <button type="button" className={s.btnPrimary} onClick={onManage}>
            Manage Sections →
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
